import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {StaticDatePicker} from '@mui/x-date-pickers/StaticDatePicker'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  styled
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {Calendar} from 'iconsax-reactjs'
import {format, isBefore, isSameDay, startOfDay} from 'date-fns'
import enUS from 'date-fns/locale/en-US'
import he from 'date-fns/locale/he'
import arSA from 'date-fns/locale/ar-SA'

const localeMap: Record<string, Locale> = {
  en: enUS,
  he: he,
  ar: arSA
}

const IconWrapper = styled('div')(({theme}) => ({
  width: 'auto',
  height: 'auto',
  marginInline: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

interface DateTimeEntry {
  date: Date
  selectedTimeSlots: string[]
}

interface DateTimePickerButtonProps {
  onSelectDates: (dates: DateTimeEntry[], message: string) => void
}

const DateTimePickerButton = ({onSelectDates}: DateTimePickerButtonProps) => {
  const {t, i18n} = useTranslation()
  const [selectedDateEntries, setSelectedDateEntries] = useState<
    DateTimeEntry[]
  >([])
  const [currentEditingDateIndex, setCurrentEditingDateIndex] = useState<
    number | null
  >(null)
  const [temporarySelectedTimeSlots, setTemporarySelectedTimeSlots] = useState<
    string[]
  >([])
  const [customTimeValue, setCustomTimeValue] = useState<Date | null>(null)
  const [isDateTimeDialogOpen, setIsDateTimeDialogOpen] = useState(false)
  const [customTimeCheckboxSelected, setCustomTimeCheckboxSelected] =
    useState(false)

  const langCode = i18n.language
  const locale = localeMap[langCode] || enUS

  const predefinedTimeSlots = [
    '08:00 - 12:00',
    '12:00 - 16:00',
    '16:00 - 20:00',
    t('fnd-assistant-dialog-date-slot-after-20')
  ]

  const openDateTimeDialog = () => {
    setIsDateTimeDialogOpen(true)
    setSelectedDateEntries([])
    setCurrentEditingDateIndex(null)
    setTemporarySelectedTimeSlots([])
    setCustomTimeCheckboxSelected(false)
    setCustomTimeValue(null)
  }

  const closeDateTimeDialog = () => {
    setIsDateTimeDialogOpen(false)
    setCurrentEditingDateIndex(null)
    setTemporarySelectedTimeSlots([])
    setCustomTimeCheckboxSelected(false)
    setCustomTimeValue(null)
  }

  const onDateSelected = (date: Date | null) => {
    if (!date) return

    const existingIndex = selectedDateEntries.findIndex((entry) =>
      isSameDay(entry.date, date)
    )

    if (existingIndex === -1) {
      const newEntry = {date, selectedTimeSlots: []}
      setSelectedDateEntries([...selectedDateEntries, newEntry])
      setCurrentEditingDateIndex(selectedDateEntries.length)
      setTemporarySelectedTimeSlots([])
    } else {
      setCurrentEditingDateIndex(existingIndex)
      setTemporarySelectedTimeSlots([
        ...selectedDateEntries[existingIndex].selectedTimeSlots
      ])
    }
  }

  const startEditingDate = (index: number) => {
    setCurrentEditingDateIndex(index)
    setTemporarySelectedTimeSlots([
      ...selectedDateEntries[index].selectedTimeSlots
    ])
  }

  const removeDateEntry = (index: number) => {
    setSelectedDateEntries(selectedDateEntries.filter((_, i) => i !== index))
    if (currentEditingDateIndex === index) {
      setCurrentEditingDateIndex(null)
      setTemporarySelectedTimeSlots([])
    }
  }

  const toggleTimeSlotSelection = (slot: string, isSelected: boolean) => {
    if (isSelected) {
      setTemporarySelectedTimeSlots((prev) => [...prev, slot])
    } else {
      setTemporarySelectedTimeSlots((prev) => prev.filter((s) => s !== slot))
    }
  }

  const addCustomTimeSlot = () => {
    if (customTimeValue) {
      const formatted = format(customTimeValue, 'HH:mm')
      if (!temporarySelectedTimeSlots.includes(formatted)) {
        setTemporarySelectedTimeSlots([
          ...temporarySelectedTimeSlots,
          formatted
        ])
        setCustomTimeValue(null)
      }
    }
  }

  const deleteCustomTimeSlot = (slot: string) => {
    setTemporarySelectedTimeSlots((prev) => prev.filter((s) => s !== slot))
  }

  const confirmTimeSlotSelection = () => {
    if (currentEditingDateIndex === null) return
    const updatedEntries = [...selectedDateEntries]
    updatedEntries[currentEditingDateIndex] = {
      ...updatedEntries[currentEditingDateIndex],
      selectedTimeSlots: temporarySelectedTimeSlots
    }
    setSelectedDateEntries(updatedEntries)
    setCurrentEditingDateIndex(null)
    setTemporarySelectedTimeSlots([])
    setCustomTimeCheckboxSelected(false)
  }

  const cancelTimeSlotSelection = () => {
    setCurrentEditingDateIndex(null)
    setTemporarySelectedTimeSlots([])
    setCustomTimeCheckboxSelected(false)
  }

  const shouldDisableDate = (date: Date) => {
    const today = startOfDay(new Date())
    const isPast = isBefore(date, today)
    const isPicked = selectedDateEntries.some((entry) =>
      isSameDay(entry.date, date)
    )
    return isPast || isPicked
  }

  const applySelectedDates = () => {
    if (selectedDateEntries.length === 0) return

    const message = selectedDateEntries
      .map((entry) => {
        const dateStr = format(entry.date, 'PP', {locale})
        const times = entry.selectedTimeSlots.join(', ')
        return `${dateStr} ${times}`
      })
      .join('\n')

    onSelectDates(selectedDateEntries, message)

    setSelectedDateEntries([])
    setIsDateTimeDialogOpen(false)
    setCustomTimeValue(null)
    setCurrentEditingDateIndex(null)
    setTemporarySelectedTimeSlots([])
    setCustomTimeCheckboxSelected(false)
  }

  const renderTimeSlotSelectionMenu = (dateIndex: number) => {
    const entry = selectedDateEntries[dateIndex]
    const date = entry.date

    return (
      <Box>
        <Typography variant="body1" marginTop={2.5}>
          {format(date, 'PP', {locale})}
        </Typography>
        <FormGroup>
          {predefinedTimeSlots.map((slot) => (
            <FormControlLabel
              key={slot}
              control={
                <Checkbox
                  size="small"
                  checked={temporarySelectedTimeSlots.includes(slot)}
                  onChange={(e) =>
                    toggleTimeSlotSelection(slot, e.target.checked)
                  }
                />
              }
              label={slot}
            />
          ))}
        </FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={customTimeCheckboxSelected}
              onChange={(e) => setCustomTimeCheckboxSelected(e.target.checked)}
            />
          }
          label={t('fnd-assistant-dialog-date-slot-custom')}
        />
        {customTimeCheckboxSelected && (
          <>
            <Box sx={{display: 'flex', alignItems: 'center', marginTop: 2}}>
              <TimePicker
                value={customTimeValue}
                views={['hours']}
                ampm={false}
                onChange={(time) => setCustomTimeValue(time)}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{width: 120}} />
                )}
              />
              <Button
                variant="contained"
                color="success"
                size="small"
                sx={{marginLeft: 1}}
                disabled={!customTimeValue}
                onClick={addCustomTimeSlot}
              >
                {t('fnd-assistant-common-add')}
              </Button>
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 2}}>
              {temporarySelectedTimeSlots
                .filter((slot) => !predefinedTimeSlots.includes(slot))
                .map((customSlot, index) => (
                  <Chip
                    key={index}
                    label={customSlot}
                    size="small"
                    onDelete={() => deleteCustomTimeSlot(customSlot)}
                  />
                ))}
            </Box>
          </>
        )}
        <Box sx={{display: 'flex', justifyContent: 'flex-start', marginTop: 2}}>
          <Button
            variant="contained"
            color="inherit"
            size="small"
            onClick={cancelTimeSlotSelection}
          >
            {t('fnd-assistant-common-cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{marginLeft: 1}}
            onClick={confirmTimeSlotSelection}
          >
            {t('fnd-assistant-common-select')}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <LocalizationProvider
      key={i18n.language}
      dateAdapter={AdapterDateFns}
      adapterLocale={locale}
    >
      <IconWrapper>
        <IconButton
          size="small"
          color="primary"
          onClick={openDateTimeDialog}
          sx={(theme) => ({
            width: 40,
            height: 40,
            padding: 0,
            color: theme.palette.common.black
          })}
        >
          <Calendar size={24} />
        </IconButton>
      </IconWrapper>
      <Dialog
        open={isDateTimeDialogOpen}
        onClose={closeDateTimeDialog}
        PaperProps={{
          sx: {
            width: 'auto',
            maxWidth: '900px'
          }
        }}
      >
        <DialogTitle>{t('fnd-assistant-dialog-date-title')}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              gap: 3
            }}
          >
            <Box sx={{flex: '0 0 auto'}}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                onChange={onDateSelected}
                renderInput={(params) => <TextField {...params} />}
                value={null}
                shouldDisableDate={shouldDisableDate}
              />
            </Box>
            <Box sx={{flex: 1, overflowY: 'auto'}}>
              {currentEditingDateIndex === null ? (
                <List>
                  {selectedDateEntries.map((entry, index) => (
                    <ListItem key={index}>
                      <Box sx={{width: '100%'}}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Typography variant="body1" marginRight={5}>
                            {format(entry.date, 'PP', {locale})}
                          </Typography>
                          <Box>
                            <IconButton onClick={() => startEditingDate(index)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton onClick={() => removeDateEntry(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            marginTop: 1
                          }}
                        >
                          {entry.selectedTimeSlots.map((time, timeIndex) => (
                            <Chip key={timeIndex} label={time} size="small" />
                          ))}
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                renderTimeSlotSelectionMenu(currentEditingDateIndex)
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            size="large"
            onClick={closeDateTimeDialog}
          >
            {t('fnd-assistant-common-cancel')}
          </Button>
          <Button
            color="primary"
            disabled={selectedDateEntries.length === 0}
            onClick={applySelectedDates}
          >
            {t('fnd-assistant-common-apply')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}

export default DateTimePickerButton
